import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback
} from "react";
import { io, Socket } from "socket.io-client";
import AuthenticationContext, {
  AUTHENTICATION_DATA_STORAGE_KEY,
  IAuthenticationData
} from "./AuthenticationContext";
import { getCache } from "../../utils/PersistenceUtils";
import CounterMsgContext from "./CounterMsgContext";
import CompanyService from "../../services/CompanyService";
import { useWinStore } from "../../components/windows/windowStore";

export enum WhatsAppNotificationEvent {
  SetOnline = "SetOnline",
  SetOffline = "SetOffline",
  CallReceived = "CallReceived",
  NotifyCallTaken = "NotifyCallTaken",
  CallTaken = "CallTaken"
}

interface SocketContextValue {
  socket: Socket | null;
  takeCall: () => void;
  setOnline: () => void;
  setOffline: () => void;
  online: boolean;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  takeCall: () => {},
  setOnline: () => {},
  setOffline: () => {},
  online: false
});

export const useSocket = () => useContext(SocketContext);

export function useSocketEvent<E = any>(
  event: string,
  handler: (payload: E) => void
) {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;
    socket.on(event, handler);
    return () => {
      socket.off(event, handler);
    };
  }, [socket, event, handler]);
}

const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  const { getCounterInbox, refreshChats } = useContext(CounterMsgContext);
  const canReceiveWhatsappNotifications =
    CompanyService.canUseWhatsappNotifications();

  const socketRef = useRef<Socket | null>(null);

  // Inicializar el estado desde localStorage para mantener el estado del usuario
  const getInitialOnlineState = () => {
    if (!canReceiveWhatsappNotifications) return false;
    const savedState = localStorage.getItem("online");
    return savedState === null ? true : savedState === "true";
  };

  const [isOnline, setIsOnline] = useState(getInitialOnlineState);

  const setOnline = useCallback(() => {
    const companyID = CompanyService.getCompanyToken()?.companyid;
    const userid = CompanyService.getCompanyToken()?.userid;

    socketRef.current?.emit(WhatsAppNotificationEvent.SetOnline, {
      companyID: companyID,
      id: parseInt(userid)
    });
    localStorage.setItem("online", "true");
    setIsOnline(true);
  }, []);

  const setOffline = useCallback(() => {
    const companyID = CompanyService.getCompanyToken()?.companyid;
    const userid = CompanyService.getCompanyToken()?.userid;

    socketRef.current?.emit(WhatsAppNotificationEvent.SetOffline, {
      companyID: companyID,
      id: parseInt(userid)
    });
    localStorage.setItem("online", "false");
    setIsOnline(false);
  }, []);

  // Handler para CallReceived que refresca ventanas de WhatsApp abiertas
  const handleCallReceived = useCallback(
    (payload: any) => {
      // Actualizar contador
      getCounterInbox?.();
      // Llamar refreshChats con optional chaining y manejo de errores
      refreshChats?.();

      // Obtener todas las ventanas de WhatsApp abiertas
      const windows = useWinStore.getState().windows;
      const whatsappWindows = windows.filter((w) =>
        w.identifier?.startsWith("whatsapp-")
      );

      if (whatsappWindows.length > 0) {
        // Extraer el personId del payload (puede venir en diferentes formatos)
        const incomingPersonId =
          payload?.PersonID ?? payload?.personId ?? payload?.PersonId;

        if (incomingPersonId) {
          // Buscar ventanas que correspondan a este personId
          // El identifier tiene formato: whatsapp-{kind}-{id}
          // Para active: whatsapp-active-{personId}
          // Para history: whatsapp-history-{messageId}
          const matchingWindows = whatsappWindows.filter((w) => {
            if (!w.identifier) return false;

            if (w.identifier.includes("-active-")) {
              const windowPersonId = w.identifier.split("-active-")[1];
              return windowPersonId === String(incomingPersonId);
            }

            // Para history, el componente WhatsappCompose ya tiene su propia lógica para filtrar
            // basándose en el personId del mensaje recibido
            return true;
          });

          // Verificar que hay ventanas de WhatsApp abiertas para este personId
          // Los componentes WhatsappCompose dentro de las ventanas ya escuchan CallReceived
          // a través de useSocketEvent, así que se refrescarán automáticamente.
          // Esta verificación asegura que el SocketProvider está sobre el provider de mensajes
          // y puede detectar las ventanas abiertas correctamente.
          if (matchingWindows.length > 0) {
            // El evento ya se propaga automáticamente a todos los listeners registrados
            // (incluyendo los componentes WhatsappCompose dentro de las ventanas)
          }
        }
      }
    },
    [getCounterInbox, refreshChats]
  );

  useEffect(() => {
    if (!isAuthenticated || !canReceiveWhatsappNotifications) return;

    const authenticationData = getCache<IAuthenticationData>(
      AUTHENTICATION_DATA_STORAGE_KEY
    );
    const socket = io(
      `${process.env.REACT_APP_PUSH_NOTIFICATIONS_URL}/WhatsAppNotifications`,
      {
        auth: { token: authenticationData?.token }
      }
    );

    socketRef.current = socket;

    // Al conectar, respetar el estado guardado en localStorage en lugar de forzar online
    const handleConnect = () => {
      const savedState = localStorage.getItem("online");
      const shouldBeOnline = savedState === null ? true : savedState === "true";

      if (shouldBeOnline) {
        setOnline();
      } else {
        // Asegurar que el estado esté sincronizado aunque no emitamos el evento
        setIsOnline(false);
      }
    };

    // Al desconectar, no cambiar el estado del usuario - solo actualizar cuando se reconecte
    const handleDisconnect = () => {
      // No sobrescribir el estado del usuario, solo marcar como desconectado temporalmente
      // El estado se restaurará cuando se reconecte según localStorage
    };

    // Listener genérico para capturar TODAS las notificaciones recibidas
    //  const handleAnyEvent = (eventName: string, ...args: any[]) => {
    // Ignorar eventos internos de socket.io
    //  if (eventName === 'connect' || eventName === 'disconnect' || eventName === 'error') {
    //    return;
    //  }
    //  refreshChats?.();
    //};

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on(WhatsAppNotificationEvent.CallReceived, handleCallReceived);
    //socket.on(WhatsAppNotificationEvent.CallTaken, handleCallTaken);

    // Registrar listener genérico para capturar todas las notificaciones
    //socket.onAny(handleAnyEvent);

    return () => {
      socket.off(WhatsAppNotificationEvent.CallReceived, handleCallReceived);
      // socket.off(WhatsAppNotificationEvent.CallTaken, handleCallTaken);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      //socket.offAny(handleAnyEvent);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [
    isAuthenticated,
    canReceiveWhatsappNotifications,
    getCounterInbox,
    setOnline,
    handleCallReceived,
    refreshChats
  ]);

  const takeCall = useCallback(() => {
    socketRef.current?.emit(WhatsAppNotificationEvent.NotifyCallTaken);
    getCounterInbox?.();
  }, [getCounterInbox]);

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        online: isOnline,
        takeCall,
        setOnline,
        setOffline
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export { SocketProvider };
export default SocketContext;
